'use client'

import Image from 'next/image'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { SITE_LOGO_PATH, SITE_NAME, SITE_TAGLINE } from '@/lib/seo'

type BrandLogoProps = {
  /** render the clover icon next to the wordmark */
  showMark?: boolean
  showWordmark?: boolean
  showTagline?: boolean
  href?: string | null
  className?: string
  markClassName?: string
  wordmarkClassName?: string
  size?: 'sm' | 'md' | 'lg'
}

const markBox = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10 sm:h-11 sm:w-11',
  lg: 'h-12 w-12',
} as const

/**
 * Ctxor brand mark + CSS wordmark.
 * Pronunciation: CON-sor (like “context”).
 */
export function BrandLogo({
  showMark = true,
  showWordmark = true,
  showTagline = false,
  href = '/',
  className,
  markClassName,
  wordmarkClassName,
  size = 'md',
}: BrandLogoProps) {
  const content = (
    <span className={cn('inline-flex items-center gap-2 min-w-0', className)}>
      {showMark ? (
        <span
          className={cn(
            'relative flex shrink-0 items-center justify-center overflow-hidden',
            markBox[size],
            markClassName,
          )}
          aria-hidden
        >
          <Image
            src={SITE_LOGO_PATH}
            alt=""
            width={48}
            height={48}
            className="h-full w-full object-contain"
          />
        </span>
      ) : null}
      {showWordmark ? (
        <span className="flex min-w-0 flex-col">
          <span
            className={cn(
              'truncate font-bold tracking-tight text-zinc-950 dark:text-zinc-50',
              size === 'sm' && 'text-sm',
              size === 'md' && 'text-base sm:text-lg',
              size === 'lg' && 'text-xl',
              wordmarkClassName,
            )}
          >
            {SITE_NAME}
          </span>
          {showTagline ? (
            <span className="truncate text-[10px] font-medium tracking-wide text-zinc-500 dark:text-zinc-400 sm:text-xs">
              {SITE_TAGLINE}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  )

  if (href) {
    return (
      <Link
        href={href}
        className="inline-flex min-w-0"
        aria-label={`${SITE_NAME} — ${SITE_TAGLINE}`}
      >
        {content}
      </Link>
    )
  }

  return content
}
